import { useEffect, useState, useMemo, useCallback } from 'react';
import { useGetInventoriesRequest } from '../../hooks/useApi';
import NoResultsIcon from '../../components/Icons/NoResultsIcon';

const isValidPosId = (value) =>
{
  if (value === null || value === undefined)
  {
    return false;
  }

  const text = String(value).trim();

  if (text === '')
  {
    return false;
  }

  if (text.toLowerCase() === 'null')
  {
    return false;
  }

  if (text.toLowerCase() === 'undefined')
  {
    return false;
  }

  return true;
};

const readPosIdFromStorage = () =>
{
  try
  {
    const raw = localStorage.getItem('changeStoreParams');

    if (!raw)
    {
      return '';
    }

    const changeStoreParams = JSON.parse(raw);
    const selectedPosId = changeStoreParams?.selectedPosId;

    return isValidPosId(selectedPosId) ? String(selectedPosId).trim() : '';
  }
  catch
  {
    return '';
  }
};

export const useInventoryScreen = () =>
{
  const [inventories, setInventories] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [filters, setFilters] = useState({
    freeText: null,
    status: null,
    type: null,
    dateFrom: null,
    dateTo: null,
  });

  const [posId, setPosId] = useState(() => readPosIdFromStorage());

  const hasStore = isValidPosId(posId);

  const [{ data, loading, error }, getInventories] = useGetInventoriesRequest(posId, {
    skip: !hasStore,
    manual: true,
  });

  useEffect(() =>
  {
    const syncPosId = () =>
    {
      setPosId(readPosIdFromStorage());
    };

    window.addEventListener('storage', syncPosId);
    window.addEventListener('changeStoreParamsUpdated', syncPosId);

    return () =>
    {
      window.removeEventListener('storage', syncPosId);
      window.removeEventListener('changeStoreParamsUpdated', syncPosId);
    };
  }, []);

  useEffect(() =>
  {
    if (!hasStore)
    {
      setInventories([]);
      return;
    }

    getInventories({
      bodyParams: {
        freeText: filters.freeText,
        status: filters.status,
        type: filters.type,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        pageIndex: 1,
        pageSize: 100,
        noCount: false,
      },
    });
  }, [hasStore, posId, refreshTrigger, filters, getInventories]);

  useEffect(() =>
  {
    if (!hasStore)
    {
      setInventories([]);
      return;
    }

    if (data?.elements)
    {
      setInventories(data.elements);
    }
    else
    {
      setInventories([]);
    }
  }, [data, hasStore]);

  const refreshInventories = useCallback(() =>
  {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const stats = useMemo(() =>
  {
    if (!hasStore)
    {
      return {
        total: 0,
        completed: 0,
        active: 0,
      };
    }

    const total = data?.totalResults || 0;

    const completed = inventories.filter(inv =>
      inv.status === 'COMPLETED'
    ).length;

    const active = inventories.filter(inv =>
      inv.status === 'ACTIVE' ||
      inv.status === 'IN_REVIEW' ||
      inv.status === 'REJECTED'
    ).length;

    return {
      total,
      completed,
      active,
    };
  }, [hasStore, data?.totalResults, inventories]);

  const inventoryError = !hasStore ? 'Select a store to see the inventories' : error;

  return {
    inventories,
    loading: hasStore ? loading : false,
    error: inventoryError,
    stats,
    posId,
    filters,
    setFilters,
    refreshInventories,
  };
};